import _ from 'lodash';

import { defineStore } from "pinia";
import {
  applySubConfigOverrides,
  catalogOptions,
  configOptionsMaxWidthForItem,
  configOptionsMinWidthForItem,
  filterAllConfigOptionsForSection,
  filterBenchtopMaterialOptions,
  filterConfigOptionsForSection,
  filterDeliveryOptions,
  filterDoorProfileOptions,
  filterEndPanelFinishOptions,
  filterHandlesOptions,
  filterHingedDoorsOptions,
  filterHingedWardrobeLayoutOptions,
  filterInteriorOptions,
  filterInteriorOptionsForSection,
  filterPanelFinishOptions,
  filterPanelsOptions,
  filterProductLayoutOptions,
  filterSubConfigOptions,
  filterTopsOptions,
  filterWalkInWardrobeLayoutOptions,
} from '@/builder/optionFilters';
import {
  logFormErrors,
  logRequestError,
  postModuleAction,
  setAuthToken,
  withCsrf,
  withQuoteContext,
} from '@/builder/api';
import { trackQuoteEvent } from '@/builder/analytics';
import {
  applyCatalogOpts,
  catalogGetters,
  catalogOptionsGetters,
  emptyCatalog,
} from '@/builder/catalog';
import { buildConfigDescription } from '@/builder/configDescription';
import {
  displayPriceIncludeGst as formatPriceIncludeGst,
  firstNamePossessive,
  shortRef,
} from '@/builder/format';
import {
  canAddMoreItems as canAddMoreQuoteItems,
  emptyItem,
  isLegacyQuote as quoteIsLegacy,
  itemBenchWidth,
  itemDoorCount,
  layoutSectionTitle,
  layoutSectionTitles,
  pageTitleHtml,
  parseProductStructure,
  parseStepsStructure,
  productHandleLabel,
  productTitleForId,
  sectionCount,
  selectedFeatures,
  selectedSectionConfigs,
  stepWithSectionsNumber,
} from '@/builder/item';
import {
  isPrepareComplete,
  prepareFieldErrors,
  RUN_OUT_DIRECTION_OPTIONS,
  TILE_TYPE_OPTIONS,
} from '@/builder/prepare';
import {
  getAvailableWidth as availableWidthForItem,
  quoteAlertForItems,
  strategyFor,
} from '@/builder/products';
import { validateItem, validateSpec as validateSpecValue } from '@/builder/validation';

function specValidationContext(store) {
  return {
    quote: store.quote,
    currentItem: store.currentItem,
    showBenchtopMaterialOptions: store.showBenchtopMaterialOptions
  };
}

function resolveDomEl(el) {
  if (_.isNil(el)) return null;

  if (typeof el.getBoundingClientRect !== 'function') {
    if (_.isArray(el)) {
      el = el[0];
    } else if (el && typeof el === 'object' && 'value' in el) {
      el = el.value;
      if (_.isArray(el)) el = el[0];
    }
    if (el && el.$el) el = el.$el;
  }

  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  return el;
}

export const useBuilderStore = defineStore("builder", {
  state: () => {
    return {

      quoteId: null,
      quote: {},
      quoteValidation: {},
      itemId: null,
      itemIndex: 0,
      items: [
        emptyItem()
      ],

      mode: 'build', // default is build, possible modes are build, quote or prepare
      section: 1,
      sectionsMax: 10,
      step: 1,
      isSaved: false,
      isSubmitted: false,
      isTouched: false,
      isCp: true,
      validation: {
        step: 0,
        results: []
      },
      token: null,
      testMode: false,
      env: 'dev',
      showSummary: false,
      inputHasInitialFocus: false,

      range: {
        handle: '',
        mainTitle: 'Builder',
        itemName: 'Item'
      },
      opts: [],
      catalog: emptyCatalog(),
      featuresFilter: [],
      featuresAvailable: {},
      featuresFilterHistory: [],
      messages: {},
      tileModType: 'benchtopMaterial',
      isPrepareComplete: false,
      prepareItemIndex: 0,
      prepareSubmitAttempted: false,
      prepareStandardLevelsItemKeys: [],
      errors: [],

      refs: {}

    };
  },
  actions: {

    saveCurrentItem(nextStep = false, exit = false, conversion = null) {

      setAuthToken(this.token);

      this.isTouched = true;
      this.validateCurrentItem();

      if (this.stepHasErrors) {
        this.scrollToRef('actionsGroup');
        return false;
      }

      if (!this.isSubmitted) {

        this.isSubmitted = true;

        const submitData = withQuoteContext({
          itemId: this.itemId ?? this.currentItem.id ?? null,
          productId: this.currentItem.productId ?? null,
          productLayoutId: this.currentItem.productLayoutId ?? null,
          label: this.currentItem.label ?? null,
          specs: this.currentItem.specs,
          firstName: this.quote.firstName,
          lastName: this.quote.lastName,
          email: this.quote.email,
          phone: this.quote.phone,
          postcode: this.quote.postcode,
          timeline: this.quote.timeline,
          featuresFilterHistory: this.featuresFilterHistory
        }, {
          token: this.token,
          quoteId: this.quote.id,
          testMode: this.testMode
        });

        postModuleAction('item/save', submitData)
        .then((response) => {

          if (response.success) {

            if (this.mode == 'prepare') {
              this.submitOrder(true);
              return true;
            }

            setTimeout(() => {

              this.isSubmitted = false;
              this.errors = [];

              this.$patch((state) => {
                state.items[state.itemIndex] = response.item;
                state.quote = response.quote;
              })

              this.setCurrentItemProp('isSaved', true);

              if (exit) window.location = '/builder/save/';

              if (nextStep) this.nextScreen();

              if (this.step == 2) this.scrollToRef('builderTop');

              this.validateCurrentItem();
              this.isSaved = true;

              this.featuresFilter = [];
              this.featuresFilterHistory = [];

              if (conversion) {
                trackQuoteEvent('mydiy_quote_saved', this.quote);
                this.quoteReceivedTrigger(true);
              }

              return true;

            }, 500);

          } else if (response.errors) {

            this.isSubmitted = false;
            this.errors = response.errors;
            logFormErrors(response);

          } else {

            this.isSubmitted = false;

          }

        })
        .catch((error) => {

          this.isSubmitted = false;
          logRequestError(error);

        });

      }

      return false;

    },

    async saveCurrentPrepareItem() {

      setAuthToken(this.token);

      this.isTouched = true;
      this.prepareSubmitAttempted = true;

      const prepareItem = this.currentPrepareItem;
      const useStandardLevels = this.currentPrepareUsesStandardLevels;
      const wasPrepareLastItem = this.isPrepareLastItem;

      if (!this.isCurrentPrepareComplete || this.isSubmitted) {
        this.scrollToRef('builderTop');
        return false;
      }

      if (!prepareItem || !prepareItem.item.id) return false;

      this.isSubmitted = true;

      const originalItemId = this.itemId;
      const quoteContext = {
        token: this.token,
        quoteId: this.quote.id,
        testMode: this.testMode
      };

      try {

        const editResponse = await postModuleAction('item/edit', withQuoteContext({
          itemId: prepareItem.item.id
        }, quoteContext));

        if (!editResponse.success) {
          this.errors = editResponse.errors ?? [];
          this.isSubmitted = false;
          return false;
        }

        let specs = {
          ...prepareItem.item.specs
        };
        if (useStandardLevels) specs.levelsOut = false;

        const response = await postModuleAction('item/save', withQuoteContext({
          itemId: prepareItem.item.id,
          productId: prepareItem.item.productId ?? null,
          productLayoutId: prepareItem.item.productLayoutId ?? null,
          label: prepareItem.item.label ?? null,
          specs: specs,
          firstName: this.quote.firstName,
          lastName: this.quote.lastName,
          email: this.quote.email,
          phone: this.quote.phone,
          postcode: this.quote.postcode,
          timeline: this.quote.timeline,
          featuresFilterHistory: this.featuresFilterHistory
        }, quoteContext));

        if (response.success) {
          this.$patch((state) => {
            state.items[prepareItem.itemIndex] = response.item;
            state.quote = response.quote;
          });
        } else {
          this.errors = response.errors ?? [];
          this.isSubmitted = false;
          return false;
        }

        if (originalItemId) {
          await postModuleAction('item/edit', withQuoteContext({
            itemId: originalItemId
          }, quoteContext));
        }

        this.isSubmitted = false;
        this.isSaved = true;
        this.prepareSubmitAttempted = false;
        this.prepareStandardLevelsItemKeys = this.prepareStandardLevelsItemKeys.filter((key) => {
          return key !== (prepareItem.item.id ?? prepareItem.item.ref);
        });

        if (wasPrepareLastItem) {
          this.isSubmitted = true;
          this.submitOrder(true);
        } else {
          if (!useStandardLevels) this.prepareItemIndex ++;
          this.scrollToRef('builderTop');
        }

        return true;

      }
      catch (error) {

        this.isSubmitted = false;
        logRequestError(error);
        return false;

      }

    },

    async actionPost(actionHandle, submitData, scrollToTop=true) {

      setAuthToken(this.token);

      if (!this.isSubmitted) {

        this.isSubmitted = true;
        if (scrollToTop) this.scrollToRef('builderTop');

        try {

          const response = await postModuleAction(actionHandle, withQuoteContext(submitData, {
            token: this.token,
            quoteId: this.quote.id,
            testMode: this.testMode
          }));

          this.isSubmitted = false;
          this.isSaved = true;

          if (response.success) {
            return response;
          } else if (response.errors) {
            logFormErrors(response);
            return false;
          } else {
            return false;
          }

        }
        catch (error) {

          this.isSubmitted = false;
          logRequestError(error);
          return false;

        }

      } else {
        return false;
      }

    },

    setCurrentItemProp(prop, val) {
      let wasEmpty = (typeof this.items[this.itemIndex][prop] !== 'undefined' && this.items[this.itemIndex][prop] !== null && this.items[this.itemIndex][prop]) ? false : true;
      this.items[this.itemIndex][prop] = val;
      this.afterCurrentItemUpdate(prop, val);
      if (this.isTouched) this.validateCurrentItem();
      if (wasEmpty) this.scrollToNext(prop);
    },

    setCurrentItemSpec(prop, val) {
      if (_.isUndefined(this.items[this.itemIndex].specs) || !_.isObject(this.items[this.itemIndex].specs)) this.items[this.itemIndex].specs = {};
      let wasEmpty = (typeof this.items[this.itemIndex].specs[prop] !== 'undefined' && this.items[this.itemIndex].specs[prop] !== null && ( this.items[this.itemIndex].specs[prop] && this.items[this.itemIndex].specs[prop] !== 117506 ) ) ? false : true;
      this.items[this.itemIndex].specs[prop] = val;
      this.afterCurrentItemUpdate(prop, val);
      if (this.isTouched) this.validateCurrentItem();
      if (wasEmpty) this.scrollToNext(prop);
    },

    setCurrentSectionSpec(prop, val) {
      if (_.isUndefined(this.items[this.itemIndex].specs.sections)) this.items[this.itemIndex].specs.sections = [];
      if (_.isUndefined(this.items[this.itemIndex].specs.sections[this.sectionIndex]) || !_.isObject(this.items[this.itemIndex].specs.sections[this.sectionIndex])) this.items[this.itemIndex].specs.sections[this.sectionIndex] = {};
      const section = this.items[this.itemIndex].specs.sections[this.sectionIndex];
      const current = section[prop];
      if (current === val || ((current === null || typeof current === 'undefined') && (val === null || typeof val === 'undefined'))) {
        return;
      }
      let wasEmpty = (typeof current !== 'undefined' && current !== null && current) ? false : true;
      section[prop] = val;
      this.afterCurrentItemUpdate(prop, val);
      if (this.isTouched) this.validateCurrentItem();
      if (wasEmpty && !_.isNull(val)) this.scrollToNext(prop, 'sections');
    },

    afterCurrentItemUpdate(prop, val) {

      // mirrored in ItemService php

      // update requireInternals
      if (prop == 'interiorId' || prop == 'configId') {
        if (parseFloat(val) == 2643) {
          this.items[this.itemIndex].specs.requireInternals = false;
        } else {
          this.items[this.itemIndex].specs.requireInternals = true;
        }
      }

      // update requireHandles
      if (prop == 'handlesId') {
        if (parseFloat(val) == 0) {
          this.items[this.itemIndex].specs.requireHandles = false;
        } else {
          this.items[this.itemIndex].specs.requireHandles = true;
        }
      }

      // update available width
      if (prop == 'depthId') {
        this.updateAvailableWidths();
      }

    },

    quoteReceivedTrigger(force = false) {

      if (this.mode == 'quote' && !this.quote.isReceived && this.quoteIsViewable) {

        try {

          const submitData = withCsrf({
            isReceived: true
          });
          if (this.token) submitData.quoteId = this.quote.id;

          postModuleAction('quote/update', submitData)
          .then((response) => {
            if (response.success) {

              trackQuoteEvent('mydiy_quote_received', this.quote);

              if (_.isObject(response)) {
                this.$patch((state) => {
                  state.quote = response.quote;
                })
              }
              return response;

            } else if (response.errors) {

              logFormErrors(response);
              return false;

            } else {

              return false;

            }

          });

        }
        catch (error) {

          logRequestError(error);
          return false;

        }

      }

    },

    hydrateCatalog(opts) {
      this.opts = opts;
      this.$patch((state) => {
        applyCatalogOpts(state.catalog, opts);
      });
    },

    featuresFilterHistoryPush() {

      if (this.featuresFilterHistory.length) {

        try {

          postModuleAction('quote/push-features-filter-history', withCsrf({
            data: this.featuresFilterHistory
          }))
          .then((response) => {
            if (response.success) {
              this.featuresFilterHistory = [];
            } else if (response.errors) {
              logFormErrors(response);
              return false;
            } else {
              return false;
            }
          });

        }
        catch (error) {

          logRequestError(error);
          return false;

        }

      }

    },

    setMode(mode) {
      if (mode == 'new') document.location.href = '/builder/';
      if (mode == 'quote' && (!this.items.length || (this.items.length <= 1 && this.currentItem.validStep <= 1))) mode = 'build';
      if (['build', 'quote', 'prepare'].indexOf(mode) !== -1) {
        if (mode == 'prepare') {
          this.prepareItemIndex = 0;
          this.prepareSubmitAttempted = false;
          this.prepareStandardLevelsItemKeys = [];
        }
        this.mode = mode;
        setTimeout(() => {
          this.scrollToRef('builderTop');
        }, 150);
      } else {
        this.mode = 'build';
      }
      if (!(this.isCp || this.testMode) && (this.currentItem.isCustom || this.quote.isCustom)) this.mode = 'quote';
      this.quoteReceivedTrigger();
    },

    setStep(step) {

      if (step == 'auto') {
        if (!_.isUndefined(this.currentItem) && !_.isNull(this.currentItem.validStep)) {
          step = this.currentItem.validStep;
        } else {
          step = 1;
        }
      }

      step = parseFloat(step);

      const prevStep = this.step;

      if (step == 0) step = 1;
      else if (step > this.stepMax && this.steps.length > 1) {
        step = this.stepMax;
        this.setMode('quote')
      }

      if (step > 1 && this.currentProductHandle.length <= 0) step = 1;

      if (!_.isUndefined(this.currentItem) && !_.isNull(this.currentItem.validStep) && step > this.currentItem.validStep) step = this.currentItem.validStep;

      if (this.step !== step) this.isTouched = false;

      this.step = step;

      if (this.step == this.stepWithSections && this.step != prevStep) this.section = 1;

    },

    nextStep() {
      this.setStep(parseFloat(this.step) + 1);
    },

    setSection(section) {
      if (section == 0) section = 1;
      else if (!_.isUndefined(this.items[this.itemIndex].specs.sections) &&
        _.isArray(this.items[this.itemIndex].specs.sections) &&
        section > this.items[this.itemIndex].specs.sections.length) section = this.items[this.itemIndex].specs.sections.length;
      if (this.section !== section) this.isTouched = false;
      this.section = section;
      this.step = this.stepWithSections;
    },

    nextSection() {
      if (this.section >= this.sectionMax) {
        this.nextStep();
      } else {
        this.setSection(parseFloat(this.section) + 1);
      }
    },

    nextScreen() {
      if (!_.isUndefined(this.steps[this.step-1].hasSections) && this.steps[this.step-1].hasSections) {
        this.nextSection();
        setTimeout(() => {
          this.scrollToRef('builderTop');
        }, 150);
      } else {
        this.nextStep();
        setTimeout(() => {
          this.scrollToRef('builderTop');
        }, 150);
      }
    },

    submitOrder(force = false) {

      if (!this.isSubmitted || force) {

        // custom delivery quote
        if (this.quote.isCustomDelivery) {
          return window.location = '/builder/submit/';

        // normal order
        } else if (this.quote.isReadyForOrder) {
          window.location = '/order/add/' + this.quote.hash + '/';
          return false;
        }

      }

    },

    validateCurrentItem() {

      // validation code uses json structure
      // code is reflected in ItemService php

      const result = validateItem({
        item: this.currentItem,
        structure: this.currentProductStructure,
        steps: this.steps,
        step: this.step,
        section: this.section,
        sectionMax: this.sectionMax,
        ctx: specValidationContext(this)
      });

      this.validation.step = result.step;
      this.validation.section = result.section;
      this.validation.results = result.results;

      return true;

    },

    validateSpec(val, rules) {
      return validateSpecValue(val, rules, specValidationContext(this));
    },

    scrollToNext(prop, scope = 'specs') {

      setTimeout(() => {

        const structure = this.currentProductStructure;

        if (structure) {

          const lastSpec = structure[Object.keys(structure)[Object.keys(structure).length - 1]];
          let spec = structure[prop];
          if (scope == 'sections') spec = structure.sections.sections[prop];

          if (!_.isUndefined(spec) && (spec.type !== 'int' || spec.handle == 'addRails' || spec.handle == 'addShelves') && prop !== 'boardId') {

            let refHandle = 'actionsGroup';
            let elExists = false;
            if (!_.isUndefined(spec.nextHandle)) {
              refHandle = spec.nextHandle;

              try {
                this.refs[refHandle].getBoundingClientRect().top;
                elExists = true;
              }
              catch(e){
                elExists = false;
              }
              if (!elExists) {
                try {
                  this.refs[refHandle][0].getBoundingClientRect().top;
                  elExists = true;
                }
                catch(e){
                  elExists = false;
                }
              }

            } else {
              let keys = Object.keys(structure);
              let i = 0;
              do {

                i++;

                let nextIndex = keys.indexOf(prop) + i;
                let nextItem = keys[nextIndex];
                let nextSpec = structure[nextItem];

                if (nextSpec && spec.step == nextSpec.step) {
                  refHandle = nextSpec.handle + 'Group';
                }

                try {
                  this.refs[refHandle].getBoundingClientRect().top;
                  elExists = true;
                }
                catch(e){
                  elExists = false;
                }
                if (!elExists) {
                  try {
                    this.refs[refHandle][0].getBoundingClientRect().top;
                    elExists = true;
                  }
                  catch(e){
                    elExists = false;
                  }
                }

                if (!elExists && nextSpec == lastSpec) {
                  refHandle = 'actionsGroup';
                  elExists = true;
                }

              }
              while (!_.isUndefined(keys[keys.indexOf(prop) + i]) && ( _.isUndefined(this.refs[refHandle]) || _.isNull(this.refs[refHandle] || !_.isElement(this.refs[refHandle])) || !elExists ) )
            }

            if (!_.isUndefined(this.refs[refHandle]) && !_.isNull(this.refs[refHandle]) && elExists) this.scrollToRef(refHandle);
            return;

          }

        }

        if (prop == 'configGroupId') {
          if (this.configOptionsForConfigGroup.length) {
            this.scrollToRef(this.config.fieldName + 'SubGroup');
            return;
          }
        }

        if ((prop == 'productId' || prop == 'productLayoutId') && !_.isNull(this.currentItem[prop])) {
          if (prop == 'productId' && !_.isUndefined(this.refs['productLayoutIdGroup']) && this.range.handle !== 'kitchens') {
            this.scrollToRef('productLayoutIdGroup');
          } else if (prop !== 'productId') {
            this.scrollToRef('actionsGroup');
          }
          return;

        }

      }, 150);

    },

    scrollToRef(ref) {

      let refEl = resolveDomEl(this.refs[ref]);
      if (!refEl) return false;

      let offsetHeight = 128;
      if (window.innerWidth <= 1200) offsetHeight = 80;
      if (document.querySelector('.site-banner')) offsetHeight += document.querySelector('.site-banner').offsetHeight;

      let newScrollY = null;
      if (ref == 'actionsGroup') {
        const builderFrameEl = resolveDomEl(this.refs['builderFrame']);
        if (!builderFrameEl) return false;
        newScrollY = window.scrollY + builderFrameEl.getBoundingClientRect().top + builderFrameEl.offsetHeight - window.innerHeight + offsetHeight - 40;
      } else {
        newScrollY = window.scrollY + refEl.getBoundingClientRect().top - offsetHeight - 20;
      }

      if (window.innerWidth <= 768) {
        newScrollY += 40;
        if (document.querySelector('.site-banner')) newScrollY -= 40;
      }

      if (newScrollY == 0) newScrollY = 1;

      if (newScrollY) {
        window.scroll(0, newScrollY);
        if (_.isFunction(refEl.focus)) refEl.focus();
      }
    },

    setItemId(itemId) {
      this.itemId = itemId;
      this.itemIndex = this.items.map(e => e.id).indexOf(this.itemId);
    },

    setItemIndexFromItemId() {
      this.itemIndex = this.items.map(e => e.id).indexOf(this.itemId);
    },

    updateUrl() {

      const sep = '/';
      let pageUrl = '/builder/';

      if (this.mode == 'build') {
        if (!_.isUndefined(this.step) && this.step > 1) pageUrl += this.step + sep;
      } else if (!_.isUndefined(this.mode) && this.mode.length) {
        pageUrl += this.mode + sep;
      }

      pageUrl += window.location.search;

    },

    displayPriceIncludeGst(value) {
      return formatPriceIncludeGst(value);
    },

    filterSubConfigs(allOpts, configGroup) {
      const hasLayoutSections = !_.isUndefined(this.currentItem.productLayoutSections) && !_.isNull(this.currentItem.productLayoutSections);
      const filteredOpts = filterSubConfigOptions(allOpts, configGroup, this.currentSectionAvailableWidth, hasLayoutSections);
      applySubConfigOverrides(this.config.options, filteredOpts);
      return filteredOpts;
    },

    updateAvailableWidths() {
      if (!_.isUndefined(this.currentItem.specs.sections) && _.isArray(this.currentItem.specs.sections) && this.currentItem.specs.sections.length) {
        this.currentItem.specs.sections.forEach((section, index) => {
          this.setCurrentSectionSpec('designWidth', this.getAvailableWidth(section, index, this.currentItem));
        });
      }
    },

    getAvailableWidth(section, sectionIndex, item) {
      return availableWidthForItem(section, sectionIndex, item);
    }

  },
  getters: {

    ...catalogGetters(),
    ...catalogOptionsGetters(),


    currentItem: (state) => {
      return state.items[state.itemIndex];
    },

    currentSection: (state) => {
      if (state.items[state.itemIndex].specs && state.items[state.itemIndex].specs.sections && _.isNumber(state.sectionIndex)) {
        return state.items[state.itemIndex].specs.sections[state.sectionIndex];
      }
      return {};
    },

    currentSectionHasCorner: (state) => {
      if (
        !_.isUndefined(state.currentItem.productLayoutSections) &&
        _.isArray(state.currentItem.productLayoutSections) &&
        state.currentItem.productLayoutSections.length) {
        const layoutSection = state.currentItem.productLayoutSections[state.sectionIndex]
        if (layoutSection.lhsType == 'corner' || layoutSection.rhsType == 'corner') {
          return true;
        }
      }
      return false;
    },

    currentSectionAvailableWidth: (state) => {

      const availableWidth = state.getAvailableWidth(state.currentSection, state.sectionIndex, state.currentItem);
      state.currentSection.designWidth = availableWidth;
      return availableWidth;

    },

    currentSectionType: (state) => {
      const layoutSection = state.currentItem.productLayoutSections[state.sectionIndex];
      if (layoutSection && layoutSection.title.includes('Island')) return 'island';
      return 'wall';
    },

    currentSectionConfigGroup: (state) => {
      if (state.currentSection.configGroupId) {
        const isConfigValid = state.configOptionsForSection.find(obj => {
          return obj.id === state.currentSection.configGroupId
        })
        const config = state.configOptions.find(obj => {
          return obj.id === state.currentSection.configGroupId
        })
        if (!_.isUndefined(config) && isConfigValid) {
          return config;
        }
      }
      return null;
    },

    currentSectionConfig: (state) => {
      if (state.currentSection.configId) {
        let isConfigValid = state.configOptionsForSection.find(obj => {
          return obj.id === state.currentSection.configId
        })
        if (!isConfigValid && state.currentSectionConfigGroup && state.currentSectionConfigGroup.configIds.length) {
          isConfigValid = state.currentSectionConfigGroup.configIds.indexOf(state.currentSection.configId) !== -1;
        }
        const config = state.configOptions.find(obj => {
          return obj.id === state.currentSection.configId
        })
        if (!_.isUndefined(config) && isConfigValid) {
          return config;
        }
      } else if (state.currentItem.specs.configId) {
        const config = state.configOptions.find(obj => {
          return obj.id === state.currentItem.specs.configId
        })
        if (!_.isUndefined(config)) {
          return config;
        }
      }
      return null;
    },

    currentSectionConfigDescription: (state) => {
      if (!state.currentSection.configId) return '';
      return buildConfigDescription({
        config: state.currentSectionConfig,
        wallSpace: state.wallSpaceOptions.find((obj) => obj.id === state.currentItem.specs.wallSpaceId),
        item: state.currentItem,
        sectionIndex: state.sectionIndex,
        availableWidth: state.currentSectionAvailableWidth,
        components: state.components,
        panelTypes: state.panelTypes
      });
    },

    selectedSections: (state) => {
      return selectedSectionConfigs(state.config.options, state.currentItem.specs.sections);
    },

    currentItemBenchWidth: (state) => {
      return itemBenchWidth(state.currentItem.specs.sections, state.configOptions);
    },

    currentItemDoorCount: (state) => {
      return itemDoorCount(
        state.currentItem.productLayoutId,
        state.productLayouts.options,
        state.hingedWardrobeLayoutOptions
      );
    },

    currentItemDoorWidth: (state) => {
      return Math.round(state.currentSectionAvailableWidth / state.currentItemDoorCount);
    },

    currentProductHandle: (state) => {
      return productHandleLabel(state.items[state.itemIndex].product);
    },

    currentProductTitle: (state) => {
      return productTitleForId(state.products.options, state.items[state.itemIndex].productId);
    },

    currentProductStructure: (state) => {
      return parseProductStructure(state.items[state.itemIndex].product);
    },

    currentSelectedFeatures: (state) => {
      return selectedFeatures({
        features: state.features,
        item: state.items[state.itemIndex],
        configOptions: state.configOptions,
        components: state.components
      });
    },

    isWardrobeBuilder: (state) => {
      return ['wardrobes'].includes(state.range.handle);
    },

    isKlBuilder: (state) => {
      return ['kitchens', 'laundries'].includes(state.range.handle);
    },

    hasPrepareStep: (state) => {
      return state.items.some((item) => strategyFor(item.productId).hasPrepareWhenLevelsOut && item.specs && item.specs.levelsOut);
    },

    prepareItems: (state) => {
      return state.items
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(({ item }) => strategyFor(item.productId).hasPrepareWhenLevelsOut && item.specs && item.specs.levelsOut);
    },

    currentPrepareItem() {
      const prepareItems = this.prepareItems;
      if (!prepareItems.length) return null;
      const prepareItemIndex = Math.min(this.prepareItemIndex, prepareItems.length - 1);
      return prepareItems[prepareItemIndex];
    },

    currentPrepareUsesStandardLevels() {
      const prepareItem = this.currentPrepareItem;
      if (!prepareItem) return false;
      const prepareItemKey = prepareItem.item.id ?? prepareItem.item.ref;
      return this.prepareStandardLevelsItemKeys.includes(prepareItemKey);
    },

    isCurrentPrepareComplete() {
      return isPrepareComplete(this.currentPrepareItem, this.currentPrepareUsesStandardLevels);
    },

    prepareStepErrors() {
      return prepareFieldErrors(this.currentPrepareItem, this.currentPrepareUsesStandardLevels);
    },

    visibleStepErrors() {
      if (this.mode == 'prepare' && this.prepareSubmitAttempted) {
        return this.prepareStepErrors;
      }
      if (this.mode == 'build' && this.isTouched && this.stepHasErrors) {
        return this.stepErrors;
      }
      return [];
    },

    isPrepareLastItem() {
      return this.prepareItemIndex >= this.prepareItems.length - 1;
    },

    productOptions: (state) => {
      return state.products.options;
    },

    productLayoutOptions: (state) => {
      return filterProductLayoutOptions(state.productLayouts, state.currentItem.productId);
    },

    kitchenLayoutOptions: (state) => {
      return state.productLayoutOptions;
    },

    laundryLayoutOptions: (state) => {
      return state.productLayoutOptions;
    },

    walkInWardrobeLayoutOptions: (state) => {
      return filterWalkInWardrobeLayoutOptions(state.productLayoutOptions);
    },

    hingedWardrobeLayoutOptions: (state) => {
      return filterHingedWardrobeLayoutOptions(state.productLayoutOptions, state.currentItem.specs.doorsMin, state.currentItem.specs.doorsMax);
    },

    interiorOptions: (state) => {
      return filterInteriorOptions(state.interior.options, state.currentItem.specs.doorsId, state.currentItem.specs.width);
    },

    interiorOptionsForSection: (state) => {
      const allOpts = state.interior.options;
      const canFilter = !_.isUndefined(allOpts)
        && !_.isUndefined(state.currentItem.productLayoutSections)
        && !_.isNull(state.currentItem.productLayoutSections)
        && _.isArray(state.currentItem.productLayoutSections);

      const filteredOpts = filterInteriorOptionsForSection({
        interiorOptions: allOpts,
        productLayoutSections: state.currentItem.productLayoutSections,
        sectionIndex: state.sectionIndex,
        sectionWidth: state.currentSection.width,
        productId: state.currentItem.productId,
        configOptions: state.config.options
      });

      if (canFilter) {
        allOpts.forEach((opt) => {
          if (filteredOpts.indexOf(opt) === -1 && state.currentSection.interiorId == opt.id) {
            state.currentSection.interiorId = null;
          }
        });
      }

      return filteredOpts;
    },

    panelsOptions: (state) => {
      return filterPanelsOptions(state.panels.options, state.currentItem.specs.width);
    },

    showEndPanelFinishOptions: (state) => {
      return state.endPanelFinishOptions.length > 1;
    },

    endPanelFinishOptions: (state) => {
      return filterEndPanelFinishOptions(state.frames.options, state.currentItem.specs.framesId);
    },

    showTopFillMessage: (state) => {
      return state.currentItem.specs.height > 2750 && state.currentItem.specs.height <= 2850;
    },

    showTopsOptions: (state) => {
      return state.topsOptions.length > 1;
    },

    topsOptions: (state) => {
      return filterTopsOptions(state.tops.options, state.currentItem.specs.height);
    },

    showDrawerTypesOptions: (state) => {
      return !!(state.currentSectionConfig && state.currentSectionConfig.hasDrawers);
    },

    showHingedHeight: (state) => {
      return !!(state.currentItem && state.currentItem.specs.heightId == 127150);
    },

    hingedDoorsOptions: (state) => {
      return filterHingedDoorsOptions(state.hingedDoors.options, state.currentItem.specs.doorsMin, state.currentItem.specs.doorsMax);
    },

    deliveryOptions: (state) => {
      return filterDeliveryOptions(state.delivery.options, state.quote && state.quote.isDeliveryOnly);
    },

    doorProfileOptions: (state) => {
      return filterDoorProfileOptions(state.doorProfile.options);
    },

    panelFinishOptions: (state) => {
      return filterPanelFinishOptions(state.panelFinish.options, state.currentItem.specs.doorProfileId);
    },

    showBenchtopMaterialOptions: (state) => {
      return state.currentItemBenchWidth > 0;
    },

    benchtopMaterialOptions: (state) => {
      return filterBenchtopMaterialOptions(state.benchtopMaterial.options);
    },

    showBenchCutoutsOption: (state) => {
      if (!_.isUndefined(state.benchtopMaterial.options) && !_.isUndefined(state.items[state.itemIndex].specs.benchId) && !_.isNull(state.items[state.itemIndex].specs.benchId)) {
        const benchMaterialOption = state.benchtopMaterial.options.filter((el) => {
          return el.id == state.items[state.itemIndex].specs.benchId
        });
        if (benchMaterialOption[0].subGroup == 'Metro Caesarstone Benchtop Range - 20mm') {
          return true;
        }
      }
      return false;
    },

    showHandlesOptions: (state) => {
      if (!_.isUndefined(state.items[state.itemIndex].specs.requireHandles) && !_.isNull(state.items[state.itemIndex].specs.requireHandles)) {
        if (state.items[state.itemIndex].specs.requireHandles === true) {
          return true;
        }
      }
      return false;
    },

    handlesOptions: (state) => {
      return filterHandlesOptions(state.handles.options);
    },

    allModOptions: (state) => {
      return catalogOptions(state.catalog[state.tileModType]);
    },

    tileTypeOptions: (state) => {
      return TILE_TYPE_OPTIONS;
    },

    configOptionsForSection: (state) => {
      let structureMinWidth = 0;
      if (state.currentProductStructure && !_.isUndefined(state.currentProductStructure.sections)) {
        structureMinWidth = state.currentProductStructure.sections.sections.width.min;
      }

      const result = filterConfigOptionsForSection({
        configOptions: state.config.options,
        productId: state.currentItem.productId,
        productLayoutSections: state.currentItem.productLayoutSections,
        sectionIndex: state.sectionIndex,
        sectionWidth: state.currentSection.width,
        itemWidth: state.currentItem.specs.width,
        availableWidth: state.currentSectionAvailableWidth,
        structureMinWidth,
        configOptionsMinWidth: state.configOptionsMinWidth,
        doorsId: state.currentItem.specs.doorsId,
        features: state.features,
        featuresFilter: state.featuresFilter
      });

      if (result.didRun) {
        state.featuresAvailable = result.featuresAvailable;
        if (result.didFilterByWidth) {
          result.allOpts.forEach((opt) => {
            const subConfigs = result.allOpts.filter((obj) => obj.configParentId === opt.id);
            state.filterSubConfigs(subConfigs, opt);
          });
        }
      }

      if (result.didMatchProduct) {
        const isConfigValid = result.validOpts.find((obj) => {
          return obj.id == state.currentSection.configId
        });
        const isConfigGroupValid = result.validOpts.find((obj) => {
          return obj.id == state.currentSection.configGroupId
        });

        if (state.stepWithSections && !_.isNil(state.currentSection.configGroupId)) {
          if (!isConfigGroupValid) {
            state.setCurrentSectionSpec('configGroupId', null);
            state.setCurrentSectionSpec('configId', null);
          }
        } else if (state.stepWithSections && !_.isNil(state.currentSection.configId) && !isConfigValid) {
          state.setCurrentSectionSpec('configId', null);
        }
      }

      return result.filteredOpts;
    },

    configOptionsForConfigGroup: (state) => {
      if (_.isNil(state.currentSectionConfigGroup)) return [];
      const subConfigs = state.config.options.filter((obj) => {
        return obj.configParentId === state.currentSectionConfigGroup.id;
      });
      return state.filterSubConfigs(subConfigs, state.currentSectionConfigGroup);
    },

    showConfigVariations: (state) => {
      return state.configOptionsForConfigGroup.length > 1;
    },

    configVariationTerm: (state) => {
      let hasDrawers = false;
      state.configOptionsForConfigGroup.forEach(config => {
        if (config.hasDrawers) hasDrawers = true;
      });
      if (hasDrawers) return 'drawer';
      return 'shelf';
    },

    allConfigOptionsForSection: (state) => {
      return filterAllConfigOptionsForSection({
        configOptions: state.config.options,
        productId: state.currentItem.productId,
        productLayoutSections: state.currentItem.productLayoutSections,
        sectionIndex: state.sectionIndex
      });
    },

    configOptionsMinWidth: (state) => {
      return configOptionsMinWidthForItem(state.allConfigOptionsForSection, state.currentItem.productId);
    },

    configOptionsMaxWidth: (state) => {
      return configOptionsMaxWidthForItem(state.allConfigOptionsForSection, state.currentItem.productId);
    },

    stepComponentName: (state) => {
      return 'Step' + state.step;
    },

    pageTitle: (state) => {
      return pageTitleHtml(state.currentItem.product, state.subTitle);
    },

    subTitle: (state) => {
      if (strategyFor(state.currentItem.productId).subtitleFromWallSpace) {
        if (state.currentItem.data.wallSpace) {
          return state.currentItem.data.wallSpace.title;
        } else {
          return '';
        }
      }
      if (!_.isNull(state.currentItem.productLayout)) {
        return state.currentItem.productLayout.title;
      }
      return '';
    },

    steps: (state) => {
      return parseStepsStructure(state.currentItem.product);
    },

    stepMax: (state) => {
      return state.steps.length;
    },

    stepWithSections: (state) => {
      return stepWithSectionsNumber(state.steps);
    },

    sectionIndex: (state) => {
      if (state.section) {
        return state.section - 1;
      }
      return 0;
    },

    sectionMax: (state) => {
      return sectionCount(state.currentItem);
    },

    sectionTitle: (state) => {
      return layoutSectionTitle(state.currentItem, state.productLayouts.options, state.sectionIndex);
    },

    sectionTitles: (state) => {
      return layoutSectionTitles(state.currentItem, state.productLayouts.options);
    },

    allValidationErrors: (state) => {
      let allErrors = [];
      for (let i = 1; i <= state.stepMax; i++) {
        if (!_.isUndefined(state.errors) && !_.isUndefined(state.errors[i])) allErrors.concat(state.errors[i]);
      }
      return allErrors;
    },

    stepHasErrors: (state) => {
      if (
        !_.isUndefined(state.validation.results) &&
        !_.isUndefined(state.validation.results[state.step - 1]) &&
        !_.isUndefined(state.validation.results[state.step - 1].errors) &&
        state.validation.results[state.step - 1].errors.length) {
        return true;
      }
      return false;
    },

    stepErrors: (state) => {
      if (state.stepHasErrors) return state.validation.results[state.step - 1].errors;
      else return [];
    },

    itemsMax: () => 10,

    canAddMoreItems: (state) => {
      return canAddMoreQuoteItems(state.items, state.itemsMax, state.sectionsMax, state.range.handle);
    },

    isLegacyQuote: (state) => {
      return quoteIsLegacy(state.quote, state.items);
    },

    requiresRecalculation: (state) => {
      return !!(!_.isUndefined(state.quote) && state.quote.requiresRecalculation);
    },

    quoteIsViewable: (state) => {
      if (!state.quote.viewable && !state.isCp) return false;
      return true;
    },

    quoteNeedsSubtotal: (state) => {
      if (!_.isUndefined(state.quote)) {
        if (state.quote.totalDeliveryDiscount != 0 && state.quote.deliveryType == 'delivery') {
          return true;
        }
        if ((state.quote.totalItems * 1.1).toFixed(2) == state.quote.total) {
          return false;
        }
      }
      return true;
    },

    quoteHasMultipleItems: (state) => {
      return !!(!_.isUndefined(state.quote) && state.items.length > 1);
    },

    quoteAlert: (state) => {
      return quoteAlertForItems(state.items);
    },

    firstNamePossesive: (state) => {
      return firstNamePossessive(state.quote.firstName);
    },

    refShort: (state) => {
      return shortRef(state.quote.ref);
    },

    runOutDirectionsOptions: () => RUN_OUT_DIRECTION_OPTIONS,

  }
});
